import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, X, AlertTriangle, Info } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type {
  CalendarCollection,
  CalendarEventInput,
  EventInstance,
  RecurrenceRule,
  RecurrenceEditScope,
  EventReminder,
} from '../types';
import { REMINDER_PRESETS, PRESET_COLORS } from '../types';
import { EditRecurringEventDialog } from './EditRecurringEventDialog';

/** Index-aligned with `Date.prototype.getDay()` (0 = Sunday). */
import type { CalendarEventTemplateDetail } from '@/lib/api';
import {
  allDayLastDate,
  browserTimeZone,
  encodeAllDayRange,
  formatUtcDateForInput,
  ruleForNewStart,
  scopeTouchesSeries,
  utcIsoToZonedInputs,
  zonedInputsToUtcIso,
} from '../utils';
import { TimezonePicker } from './TimezonePicker';
import { RecurrenceBuilder } from './RecurrenceBuilder';
import { ColorPicker } from './ColorPicker';
import { TemplatePicker } from './TemplatePicker';
import {
  parseRruleToRule,
  buildRruleString,
  findConflicts,
  formatTimeInZone,
  type BreakWarning,
} from '../utils';

export interface EventDialogSettings {
  defaultCalendarId: string | null;
  defaultEventDuration: number;
  defaultEventReminder: number | null;
  defaultEventStatus: 'confirmed' | 'tentative';
  defaultEventColor: string | null;
  showConflictWarnings: boolean;
  allowDoubleBooking: boolean;
  timeFormat: '12h' | '24h';
  minBreakBetweenEvents: number;
}

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    input: CalendarEventInput & {
      rruleObj?: RecurrenceRule | null;
      /**
       * Which occurrences an edit applies to. Undefined when creating, or when
       * editing a non-recurring event — there is nothing to scope.
       */
      scope?: RecurrenceEditScope;
    },
  ) => Promise<void>;
  calendars: CalendarCollection[];
  defaultCalendarId: string | null;
  initialData?: Partial<CalendarEventInput>;
  editEvent?: EventInstance | null;
  title?: string;
  templates?: CalendarEventTemplateDetail[];
  onSaveAsTemplate?: (name: string) => void;
  settings?: EventDialogSettings;
  existingEvents?: EventInstance[];
  /** @deprecated Use settings.defaultEventDuration instead */
  defaultEventDuration?: number;
  /** @deprecated Use settings.defaultEventReminder instead */
  defaultEventReminder?: number | null;
  /** @deprecated Use settings.defaultEventStatus instead */
  defaultEventStatus?: 'confirmed' | 'tentative';
  /** @deprecated Use settings.defaultEventColor instead */
  defaultEventColor?: string | null;
  /** @deprecated Use settings.showConflictWarnings instead */
  showConflictWarnings?: boolean;
  /** @deprecated Use settings.allowDoubleBooking instead */
  allowDoubleBooking?: boolean;
  /** @deprecated Use settings.timeFormat instead */
  timeFormat?: '12h' | '24h';
  /** @deprecated Use settings.minBreakBetweenEvents instead */
  minBreakBetweenEvents?: number;
}

function formatDateForInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatTimeForInput(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** "09:00" + 90 -> "10:30". Wraps within the day; callers roll the date. */
function minutesLater(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Local wall-clock instant from the paired date + time inputs. */
function wallClock(date: string, time: string): Date {
  return new Date(`${date}T${time || '00:00'}:00`);
}

function addDaysToInput(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return formatDateForInput(d);
}

export function EventDialog({
  open,
  onClose,
  onSave,
  calendars,
  defaultCalendarId,
  initialData,
  editEvent,
  title: dialogTitle,
  templates,
  onSaveAsTemplate,
  settings: settingsProp,
  existingEvents = [],
  defaultEventDuration: legacyDefaultEventDuration,
  defaultEventReminder: legacyDefaultEventReminder,
  defaultEventStatus: legacyDefaultEventStatus,
  defaultEventColor: legacyDefaultEventColor,
  showConflictWarnings: legacyShowConflictWarnings,
  allowDoubleBooking: legacyAllowDoubleBooking,
  timeFormat: legacyTimeFormat,
  minBreakBetweenEvents: legacyMinBreakBetweenEvents,
}: EventDialogProps) {
  // Resolve settings: prefer the grouped settings object, fall back to individual props
  const defaultEventDuration =
    settingsProp?.defaultEventDuration ?? legacyDefaultEventDuration ?? 60;
  const defaultEventReminder =
    settingsProp?.defaultEventReminder ?? legacyDefaultEventReminder ?? null;
  const defaultEventStatus =
    settingsProp?.defaultEventStatus ?? legacyDefaultEventStatus ?? 'confirmed';
  const defaultEventColor = settingsProp?.defaultEventColor ?? legacyDefaultEventColor ?? null;
  const showConflictWarnings =
    settingsProp?.showConflictWarnings ?? legacyShowConflictWarnings ?? true;
  const allowDoubleBooking = settingsProp?.allowDoubleBooking ?? legacyAllowDoubleBooking ?? true;
  const timeFormat = settingsProp?.timeFormat ?? legacyTimeFormat ?? '12h';
  const minBreakBetweenEvents =
    settingsProp?.minBreakBetweenEvents ?? legacyMinBreakBetweenEvents ?? 0;
  const [saving, setSaving] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [isAllDay, setIsAllDay] = useState(false);
  const [timezone, setTimezone] = useState(browserTimeZone());
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [showColor, setShowColor] = useState(false);
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'tentative' | 'cancelled'>('confirmed');
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(null);
  const [reminders, setReminders] = useState<EventReminder[]>([
    { type: 'notification', minutesBefore: 15 },
  ]);
  const [tags, setTags] = useState('');
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [pendingInput, setPendingInput] = useState<
    (CalendarEventInput & { rruleObj?: RecurrenceRule | null }) | null
  >(null);

  /**
   * Moving the start moves the end with it, preserving duration — the
   * behaviour every mainstream calendar has.
   *
   * Without it, rescheduling a 08:00 Monday meeting to 09:00 Tuesday is four
   * edits instead of two, and the two intermediate states are invalid: after
   * changing the start date alone the event ends BEFORE it begins, which the
   * save guard rejects. Users met an error message for a sequence of edits
   * they had not finished making.
   *
   * The shift is applied to the END, not recomputed from a default duration,
   * so a deliberately long or short event keeps its length.
   */
  const shiftEndPreservingDuration = (
    nextStartDate: string,
    nextStartTime: string,
    currentStartDate: string,
    currentStartTime: string,
  ) => {
    const prevStart = new Date(`${currentStartDate}T${currentStartTime || '00:00'}:00`);
    const nextStart = new Date(`${nextStartDate}T${nextStartTime || '00:00'}:00`);
    const prevEnd = new Date(`${endDate}T${endTime || '00:00'}:00`);
    // Half-entered values (an empty date mid-typing) produce NaN; leave the end
    // alone rather than moving it somewhere arbitrary.
    if ([prevStart, nextStart, prevEnd].some((d) => Number.isNaN(d.getTime()))) return;

    const nextEnd = new Date(prevEnd.getTime() + (nextStart.getTime() - prevStart.getTime()));
    setEndDate(formatDateForInput(nextEnd));
    setEndTime(formatTimeForInput(nextEnd));
  };

  /**
   * Keeps the end at or after the start when the END is the field being edited.
   *
   * The start handlers above drag the end along; this is the other half. Until
   * now an end earlier than the start was simply allowed into the form and met
   * a destructive toast on save, which is a worse way to learn about it than
   * not being able to express it.
   *
   * An end TIME earlier than the start on the SAME day is read as an overnight
   * event and rolls the end date forward — 22:00-02:00 is a real thing users
   * type, and every mainstream calendar treats it that way. An end DATE moved
   * before the start date has no such reading, so it clamps.
   */
  const normalizeEnd = (nextEndDate: string, nextEndTime: string) => {
    const start = wallClock(startDate, isAllDay ? '00:00' : startTime);
    let date = nextEndDate;
    let time = nextEndTime;
    if (Number.isNaN(start.getTime()) || Number.isNaN(wallClock(date, time).getTime())) {
      setEndDate(date);
      setEndTime(time);
      return;
    }

    if (date < startDate) date = startDate;
    // All-day compares dates only; a same-day end is valid (a one-day event).
    if (!isAllDay && wallClock(date, time) < start) {
      date = date === startDate ? addDaysToInput(startDate, 1) : startDate;
      if (wallClock(date, time) < start) time = minutesLater(startTime, defaultEventDuration);
    }
    setEndDate(date);
    setEndTime(time);
  };

  const handleEndDateChange = (value: string) => normalizeEnd(value, endTime);
  const handleEndTimeChange = (value: string) => normalizeEnd(endDate, value);

  /**
   * Toggling all-day is not just a flag — the two modes hold different fields.
   *
   * Turning it OFF used to leave whatever times were last in state, so an event
   * that arrived from a provider as all-day became a 00:00-23:59 "timed" event.
   * Seed a real slot instead, and collapse a multi-day timed event's end onto
   * the start date when turning it ON so the range means what it shows.
   */
  const handleAllDayChange = (next: boolean) => {
    setIsAllDay(next);
    if (next) {
      // Collapse a range that spans two dates only because it runs past
      // midnight: a 22:00->02:00 event is ONE evening, so turning it all-day
      // should give a one-day block rather than a two-day one. A genuinely
      // multi-day range keeps its span. The comment here used to promise this
      // while the code only handled the unreachable end-before-start case.
      const spansOneNight =
        endDate !== startDate &&
        wallClock(endDate, endTime).getTime() - wallClock(startDate, startTime).getTime() <=
          24 * 60 * 60 * 1000;
      if (endDate < startDate || spansOneNight) setEndDate(startDate);
      return;
    }
    const degenerate =
      !startTime || !endTime || wallClock(endDate, endTime) <= wallClock(startDate, startTime);
    if (degenerate) {
      const seedStart = startTime && startTime !== '00:00' ? startTime : '09:00';
      setStartTime(seedStart);
      setEndDate(startDate);
      setEndTime(minutesLater(seedStart, defaultEventDuration));
    }
  };

  const handleStartDateChange = (value: string) => {
    shiftEndPreservingDuration(value, startTime, startDate, startTime);
    setStartDate(value);
    // The weekly rule is NOT rewritten here. Which occurrences an edit applies
    // to is unknown until the user answers the scope prompt, and rewriting
    // eagerly had two costs: the recurrence builder visibly flipped to a day
    // the user never chose, and a later "All events" — to propagate a title
    // fix, say — would then also move the whole series onto that day. Worse,
    // once series-only changes are refused for a single-occurrence edit, an
    // auto-flipped rule made moving ONE occurrence fail with a complaint about
    // a repeat rule the user never touched. It now happens at save, only for a
    // scope that actually reaches the series — see commitSave.
  };

  const handleStartTimeChange = (value: string) => {
    shiftEndPreservingDuration(startDate, value, startDate, startTime);
    setStartTime(value);
  };

  // Initialize form only on open transition (not on every rerender while open)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Dialog just opened — initialize form
      if (editEvent) {
        setEventTitle(editEvent.title);
        const start = new Date(editEvent.startAt);
        if (editEvent.isAllDay) {
          // Read back in UTC to match how it was stored, and show the
          // INCLUSIVE last day — the stored end is the exclusive next
          // midnight, so a one-day event would otherwise open showing a
          // two-day range and grow by a day on every save.
          setStartDate(formatUtcDateForInput(start));
          setEndDate(formatUtcDateForInput(allDayLastDate(editEvent.startAt, editEvent.endAt)));
          // Times are hidden while all-day is on, but seed them so toggling
          // it off yields a sane slot rather than 00:00-00:00.
          setStartTime('09:00');
          setEndTime(minutesLater('09:00', defaultEventDuration));
        } else {
          // Rendered in the VIEWER's zone — the same times the grid shows.
          //
          // Rendering in the event's own stored zone was wrong, and visibly so:
          // `vault_calendar_events.timezone` is NOT NULL DEFAULT 'UTC', so for
          // almost every existing event it holds 'UTC' because nothing ever set
          // it, not because the event was authored in UTC. Trusting it made a
          // 3pm Denver meeting open as 9pm. A calendar shows you your own
          // times; the zone is metadata, and the picker below is how you change
          // what the typed times MEAN.
          const zone = browserTimeZone();
          const s0 = utcIsoToZonedInputs(editEvent.startAt, zone);
          const e0 = utcIsoToZonedInputs(editEvent.endAt, zone);
          setStartDate(s0.date);
          setStartTime(s0.time);
          setEndDate(e0.date);
          setEndTime(e0.time);
        }
        setTimezone(browserTimeZone());
        setIsAllDay(editEvent.isAllDay);
        setDescription(editEvent.description || '');
        setLocation(editEvent.location || '');
        setCalendarId(editEvent.calendarId);
        setColor(editEvent.color);
        setShowColor(!!editEvent.color);
        setUrl(editEvent.url || '');
        setStatus(editEvent.status);
        setRecurrence(parseRruleToRule(editEvent.rrule));
        setReminders(editEvent.reminders || []);
        setTags(editEvent.tags?.join(', ') || '');
      } else {
        setEventTitle('');
        const now = new Date();
        const startStr = initialData?.startAt
          ? formatDateForInput(new Date(initialData.startAt))
          : formatDateForInput(now);
        setStartDate(startStr);
        const startDt = initialData?.startAt ? new Date(initialData.startAt) : null;
        setStartTime(startDt ? formatTimeForInput(startDt) : '09:00');

        if (initialData?.endAt) {
          setEndDate(formatDateForInput(new Date(initialData.endAt)));
          setEndTime(formatTimeForInput(new Date(initialData.endAt)));
        } else {
          // Compute end time from start + defaultEventDuration
          const baseStart =
            startDt ||
            (() => {
              const d = new Date(now);
              d.setHours(9, 0, 0, 0);
              return d;
            })();
          const computedEnd = new Date(baseStart.getTime() + defaultEventDuration * 60000);
          setEndDate(formatDateForInput(computedEnd));
          setEndTime(formatTimeForInput(computedEnd));
        }

        setTimezone(browserTimeZone());
        setIsAllDay(initialData?.isAllDay || false);
        setDescription('');
        setLocation('');
        setCalendarId(defaultCalendarId || calendars[0]?.id || '');
        setColor(defaultEventColor);
        setShowColor(!!defaultEventColor);
        setUrl('');
        setStatus(defaultEventStatus);
        setRecurrence(null);
        setReminders(
          defaultEventReminder != null
            ? [{ type: 'notification', minutesBefore: defaultEventReminder }]
            : [{ type: 'notification', minutesBefore: 15 }],
        );
        setTags('');
      }
    }
    prevOpenRef.current = open;
  }, [
    open,
    editEvent,
    initialData,
    defaultCalendarId,
    calendars,
    defaultEventDuration,
    defaultEventReminder,
    defaultEventStatus,
    defaultEventColor,
  ]);

  const handleSave = async () => {
    if (!eventTitle.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    if (!calendarId) {
      toast({ title: 'Calendar required', variant: 'destructive' });
      return;
    }

    // Server requires full ISO 8601 with offset (z.iso.datetime({ offset: true })).
    // <input type="date"> + <input type="time"> give us a naive local datetime;
    // round through Date so the browser's timezone is applied and toISOString
    // emits the canonical UTC form.
    //
    // All-day is NOT an instant and does not go through local time at all — see
    // encodeAllDayRange. It previously used local midnight with a 23:59:59.999
    // end, which put every user east of UTC one day early both in the grid and
    // in what was pushed to Outlook and iCloud.
    const { startAt, endAt } = isAllDay
      ? encodeAllDayRange(startDate, endDate)
      : {
          startAt: zonedInputsToUtcIso(startDate, startTime, timezone),
          endAt: zonedInputsToUtcIso(endDate, endTime, timezone),
        };

    if (new Date(endAt) < new Date(startAt)) {
      toast({ title: 'End must be after start', variant: 'destructive' });
      return;
    }

    const input: CalendarEventInput & { rruleObj?: RecurrenceRule | null } = {
      calendarId,
      title: eventTitle.trim(),
      description: description || undefined,
      location: location || undefined,
      startAt,
      endAt,
      isAllDay,
      // Sent only when the user actually picked a zone. Otherwise opening an
      // event while travelling and saving an unrelated field would stamp
      // wherever you happen to be onto it.
      timezone: isAllDay || timezone === browserTimeZone() ? undefined : timezone,
      status,
      color: showColor ? color : null,
      url: url || undefined,
      isRecurring: !!recurrence,
      rrule: recurrence ? buildRruleString(recurrence) : null,
      reminders,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      rruleObj: recurrence,
    };

    // A recurring event has to say WHICH occurrences the edit applies to.
    // Asked here, AFTER the user has finished editing, rather than on open:
    // at open time nobody knows yet which fields they will touch.
    //
    // This used to send a hardcoded `scope: 'all'` with no prompt, so changing
    // one occurrence rewrote the whole series.
    if (editEvent?.isRecurring) {
      setPendingInput(input);
      setScopeDialogOpen(true);
      return;
    }

    await commitSave(input);
  };

  const commitSave = async (
    input: CalendarEventInput & { rruleObj?: RecurrenceRule | null },
    scope?: RecurrenceEditScope,
  ) => {
    setSaving(true);
    try {
      // A series master starting on a day its own rule excludes is invalid:
      // Graph either refuses the push (weekly) or accepts it and silently
      // discards the move (monthly), and locally the series expands from a
      // start no occurrence matches. Only a series-wide scope moves the
      // master's start, so only that needs the rule brought along with it.
      const adjusted = scopeTouchesSeries(scope)
        ? ruleForNewStart(input.rrule ?? null, input.startAt, {
            timeZone: timezone,
            previousStartIso: editEvent?.startAt,
            isAllDay: input.isAllDay,
          })
        : ({ kind: 'unchanged' } as const);

      if (adjusted.kind === 'unsupported') {
        // Refused, not submitted. Saving anyway would send a start the rule
        // contradicts — which Outlook discards silently, so the user would see
        // a successful save and no change.
        toast({
          title: 'Cannot move the whole series',
          description: adjusted.reason,
          variant: 'destructive',
        });
        return;
      }

      const payload = adjusted.kind === 'rewritten' ? { ...input, rrule: adjusted.rrule } : input;
      await onSave(scope ? { ...payload, scope } : payload);
      onClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save event',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
      setScopeDialogOpen(false);
      setPendingInput(null);
    }
  };

  const addReminder = () => {
    setReminders((prev) => [...prev, { type: 'notification', minutesBefore: 15 }]);
  };

  const removeReminder = (index: number) => {
    setReminders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSelectTemplate = (template: CalendarEventTemplateDetail) => {
    setEventTitle(template.title);
    setDescription(template.description || '');
    setLocation(template.location || '');
    setIsAllDay(template.isAllDay);
    setStatus(template.status);
    setColor(template.color);
    setShowColor(!!template.color);
    setUrl(template.url || '');
    setRecurrence(parseRruleToRule(template.rrule));
    setReminders(template.reminders || [{ type: 'notification', minutesBefore: 15 }]);
    setTags(template.tags?.join(', ') || '');
    if (template.calendarId) setCalendarId(template.calendarId);

    // Set duration-based end time from start
    if (!isAllDay && template.durationMinutes) {
      const start = new Date(`${startDate}T${startTime}:00`);
      const end = new Date(start.getTime() + template.durationMinutes * 60000);
      setEndDate(formatDateForInput(end));
      setEndTime(formatTimeForInput(end));
    }
  };

  // Conflict detection
  const conflicts = useMemo(() => {
    if (!showConflictWarnings || isAllDay || !startDate || !startTime || !endDate || !endTime) {
      return [];
    }
    // Read in the event's OWN zone, exactly as handleSave encodes it. Parsing
    // these as browser-local meant a foreign-zone event was compared at the
    // wrong instant: a Tokyo meeting genuinely overlapping an existing one
    // showed no warning, and with double-booking disabled a non-overlapping one
    // could be blocked from saving.
    const startAt = zonedInputsToUtcIso(startDate, startTime, timezone);
    const endAt = zonedInputsToUtcIso(endDate, endTime, timezone);
    if (new Date(endAt) <= new Date(startAt)) return [];
    return findConflicts(startAt, endAt, existingEvents, editEvent?.eventId);
  }, [
    showConflictWarnings,
    isAllDay,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    existingEvents,
    editEvent,
  ]);

  // Break gap warning
  const breakWarnings = useMemo<BreakWarning[]>(() => {
    if (
      minBreakBetweenEvents <= 0 ||
      isAllDay ||
      !startDate ||
      !startTime ||
      !endDate ||
      !endTime
    ) {
      return [];
    }
    // Read in the event's OWN zone, as handleSave and the overlap check both
    // do. Parsing these as browser-local compared the wrong instant against
    // existing events, so a foreign-zone event got break warnings it should not
    // have — or missed ones it should — while its overlap warnings were right.
    const newStart = new Date(zonedInputsToUtcIso(startDate, startTime, timezone));
    const newEnd = new Date(zonedInputsToUtcIso(endDate, endTime, timezone));
    if (newEnd <= newStart) return [];
    const minGapMs = minBreakBetweenEvents * 60 * 1000;
    const warnings: BreakWarning[] = [];
    for (const e of existingEvents) {
      if (e.isAllDay) continue;
      if (editEvent && e.eventId === editEvent.eventId) continue;
      const eStart = new Date(e.startAt);
      const eEnd = new Date(e.endAt);
      // Check if existing event ends just before the new event starts
      const gapBefore = newStart.getTime() - eEnd.getTime();
      if (gapBefore > 0 && gapBefore < minGapMs) {
        warnings.push({
          eventBefore: e,
          eventAfter: e /* placeholder */,
          gapMinutes: Math.round(gapBefore / 60000),
        });
      }
      // Check if existing event starts just after the new event ends
      const gapAfter = eStart.getTime() - newEnd.getTime();
      if (gapAfter > 0 && gapAfter < minGapMs) {
        warnings.push({ eventBefore: e, eventAfter: e, gapMinutes: Math.round(gapAfter / 60000) });
      }
    }
    return warnings;
  }, [
    minBreakBetweenEvents,
    isAllDay,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    existingEvents,
    editEvent,
  ]);

  const saveBlocked = !allowDoubleBooking && conflicts.length > 0;

  const [templateNamePrompt, setTemplateNamePrompt] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const handleSaveAsTemplate = () => {
    if (!templateName.trim()) return;
    onSaveAsTemplate?.(templateName.trim());
    setTemplateNamePrompt(false);
    setTemplateName('');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle || (editEvent ? 'Edit Event' : 'New Event')}</DialogTitle>
            <DialogDescription>
              {editEvent ? 'Update the event details.' : 'Create a new calendar event.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Template picker (create mode only) */}
            {!editEvent && templates && templates.length > 0 && (
              <TemplatePicker templates={templates} onSelect={handleSelectTemplate} />
            )}

            {/* Title */}
            <div>
              <Label>Title</Label>
              <Input
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="Event title"
                autoFocus
              />
            </div>

            {/* All Day */}
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={isAllDay}
                onCheckedChange={(c) => handleAllDayChange(c === true)}
              />
              <span className="text-sm">All day</span>
            </label>

            {/* Start */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="event-start-date" className="text-xs">
                  Start date
                </Label>
                <Input
                  id="event-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="text-sm"
                />
              </div>
              {!isAllDay && (
                <div>
                  <Label htmlFor="event-start-time" className="text-xs">
                    Start time
                  </Label>
                  <Input
                    id="event-start-time"
                    type="time"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* End */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="event-end-date" className="text-xs">
                  End date
                </Label>
                <Input
                  id="event-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  className="text-sm"
                />
              </div>
              {!isAllDay && (
                <div>
                  <Label htmlFor="event-end-time" className="text-xs">
                    End time
                  </Label>
                  <Input
                    id="event-end-time"
                    type="time"
                    value={endTime}
                    onChange={(e) => handleEndTimeChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* Timezone — timed events only; an all-day event is a date. */}
            {!isAllDay && (
              <div>
                <Label htmlFor="event-timezone" className="text-xs">
                  Timezone
                </Label>
                <TimezonePicker id="event-timezone" value={timezone} onChange={setTimezone} />
              </div>
            )}

            {/* Conflict warning */}
            {conflicts.length > 0 && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    <p className="font-medium">
                      {conflicts.length === 1
                        ? 'Scheduling conflict'
                        : `${conflicts.length} scheduling conflicts`}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {conflicts.map((c) => (
                        <li key={c.id} className="text-xs">
                          {/* The picker's zone, not the browser's — the form
                              above reads and writes in `timezone`, so listing
                              the clash in local time put two genuinely
                              overlapping events hours apart on screen. */}
                          {c.title} ({formatTimeInZone(c.startAt, timezone, timeFormat)} –{' '}
                          {formatTimeInZone(c.endAt, timezone, timeFormat)})
                        </li>
                      ))}
                    </ul>
                    {saveBlocked && (
                      <p className="text-xs mt-1 font-medium">
                        Double booking is disabled. Adjust the time to save.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Break gap warning */}
            {breakWarnings.length > 0 && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    {breakWarnings.map((w, i) => (
                      <p key={i} className="text-xs">
                        Only {w.gapMinutes}min between this and {w.eventBefore.title}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Location */}
            <div>
              <Label className="text-xs">Location</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Add location"
                className="text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add description"
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Calendar */}
            <div>
              <Label className="text-xs">Calendar</Label>
              <Select value={calendarId} onValueChange={setCalendarId}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Select calendar" />
                </SelectTrigger>
                <SelectContent>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full inline-block"
                          style={{ backgroundColor: cal.color }}
                        />
                        {cal.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="tentative">Tentative</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Recurrence */}
            <RecurrenceBuilder value={recurrence} onChange={setRecurrence} />

            {/* Reminders */}
            <div>
              <Label className="text-xs">Reminders</Label>
              <div className="space-y-2 mt-1">
                {reminders.map((rem, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Select
                      value={String(rem.minutesBefore)}
                      onValueChange={(v) => {
                        const next = [...reminders];
                        next[i] = { ...next[i], minutesBefore: parseInt(v, 10) };
                        setReminders(next);
                      }}
                    >
                      <SelectTrigger className="text-xs h-8 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_PRESETS.map((p) => (
                          <SelectItem key={p.minutes} value={String(p.minutes)}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeReminder(i)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addReminder} className="text-xs">
                  Add reminder
                </Button>
              </div>
            </div>

            {/* Color override */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <Checkbox
                  checked={showColor}
                  onCheckedChange={(c) => {
                    setShowColor(c === true);
                    if (!c) setColor(null);
                  }}
                />
                <span className="text-xs">Custom color</span>
              </label>
              {showColor && <ColorPicker value={color || PRESET_COLORS[0]} onChange={setColor} />}
            </div>

            {/* URL */}
            <div>
              <Label className="text-xs">URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="text-sm"
              />
            </div>

            {/* Tags */}
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="meeting, client, ..."
                className="text-sm"
              />
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            {onSaveAsTemplate && !templateNamePrompt && (
              <Button
                variant="secondary"
                size="sm"
                className="mr-auto text-xs"
                onClick={() => setTemplateNamePrompt(true)}
                disabled={saving || !eventTitle.trim()}
              >
                Save as Template
              </Button>
            )}
            {templateNamePrompt && (
              <div className="mr-auto flex items-center gap-1">
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="h-7 text-xs w-36"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveAsTemplate();
                    if (e.key === 'Escape') setTemplateNamePrompt(false);
                  }}
                />
                <Button size="sm" className="h-7 text-xs" onClick={handleSaveAsTemplate}>
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setTemplateNamePrompt(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || saveBlocked}
              title={saveBlocked ? 'Resolve scheduling conflicts before saving' : undefined}
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editEvent ? 'Save Changes' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditRecurringEventDialog
        open={scopeDialogOpen}
        title={eventTitle}
        onCancel={() => {
          setScopeDialogOpen(false);
          setPendingInput(null);
        }}
        onConfirm={(scope) => {
          if (pendingInput) void commitSave(pendingInput, scope);
        }}
      />
    </>
  );
}
